/**
 * SquadArena.jsx  — 10K Squad PVP Battle Arena
 *
 * Drop into your router:
 *   import SquadArena from '@/games/SquadArena'
 *   <Route path="/games/arena" element={<SquadArena />} />
 *
 * Requires birdEngine.js in the same folder.
 * Bird art is served from public/images/{parrot,ngml,legend,diamond,insane,member}.png
 */

import React, {
  useState, useEffect, useRef, useCallback, CSSProperties,
} from 'react';

// ─── Bird Drawing Engine ────────────────────────────────────────────────────
// Pure canvas functions — no React deps, safe to call from useEffect / rAF

// 6 real images from public/images/, cycled across 8 bird types
const BIRD_IMAGES = [
  '/images/parrot.png',   // 0 Flame Wing
  '/images/ngmi.png',     // 1 Sky Raider
  '/images/legend.png',   // 2 Shadow Hawk
  '/images/diamond.png',  // 3 Golden Eagle
  '/images/insane.png',   // 4 Storm Crow
  '/images/member.png',   // 5 Crimson Jay
  '/images/parrot.png',   // 6 Void Raven  (reuse, different glow applied in component)
  '/images/ngmi.png',     // 7 Sunbird     (reuse)
];

export const BIRD_TYPES = [
  { name: 'Flame Wing',   primary: '#ff6b1a', secondary: '#ffd700', eye: '#ff0040', beak: '#ffcc00', crest: true,  image: BIRD_IMAGES[0], glow: '#ff6b1a' },
  { name: 'Sky Raider',   primary: '#00c8ff', secondary: '#7b2fff', eye: '#ffffff', beak: '#ffcc44', crest: false, image: BIRD_IMAGES[1], glow: '#00c8ff' },
  { name: 'Shadow Hawk',  primary: '#1e1e3f', secondary: '#a855f7', eye: '#ff3d6e', beak: '#888888', crest: true,  image: BIRD_IMAGES[2], glow: '#a855f7' },
  { name: 'Golden Eagle', primary: '#c8a000', secondary: '#8b5e00', eye: '#ff6600', beak: '#ffaa00', crest: false, image: BIRD_IMAGES[3], glow: '#ffd700' },
  { name: 'Storm Crow',   primary: '#2a3a55', secondary: '#00ff88', eye: '#00ff88', beak: '#88aacc', crest: true,  image: BIRD_IMAGES[4], glow: '#00ff88' },
  { name: 'Crimson Jay',  primary: '#cc2244', secondary: '#ff88aa', eye: '#ffffff', beak: '#ffcc00', crest: true,  image: BIRD_IMAGES[5], glow: '#ff3d6e' },
  { name: 'Void Raven',   primary: '#0a0a1a', secondary: '#6600ff', eye: '#6600ff', beak: '#333366', crest: false, image: BIRD_IMAGES[0], glow: '#6600ff' },
  { name: 'Sunbird',      primary: '#ffdd00', secondary: '#ff8800', eye: '#ff0000', beak: '#ff6600', crest: true,  image: BIRD_IMAGES[1], glow: '#ffdd00' },
];

/**
 * Draw a bird onto a canvas element (used for the hero canvas on the connect page).
 */
export function drawBird(canvas: HTMLCanvasElement, typeIdx: number, frame: number, flipped = false, glowColor: string | null = null) {
  if (!canvas) return;
  const ctx = canvas.getContext('2d')!;
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);
  const bird = BIRD_TYPES[typeIdx % BIRD_TYPES.length];
  const S = w / 140;
  const bounce = Math.sin(frame * 0.05) * 3 * S;
  ctx.save();
  if (flipped) { ctx.translate(w, 0); ctx.scale(-1, 1); }
  ctx.translate(w * 0.5, h * 0.5 + bounce);
  if (glowColor) { ctx.shadowBlur = 18 * S; ctx.shadowColor = glowColor; }
  const wingBeat = Math.sin(frame * 0.09) * 4 * S;
  ctx.beginPath();
  ctx.ellipse(16 * S, 6 * S + wingBeat, 20 * S, 9 * S, -0.35, 0, Math.PI * 2);
  ctx.fillStyle = bird.secondary; ctx.fill();
  ctx.beginPath();
  ctx.ellipse(-16 * S, 6 * S - wingBeat, 20 * S, 9 * S, 0.35, 0, Math.PI * 2);
  ctx.fillStyle = bird.secondary; ctx.fill();
  ctx.beginPath();
  ctx.moveTo(-8 * S, 18 * S);
  ctx.lineTo(-18 * S, 30 * S + Math.sin(frame * 0.07) * 2 * S);
  ctx.lineTo(-4 * S, 22 * S);
  ctx.lineTo(4 * S, 28 * S + Math.cos(frame * 0.06) * 2 * S);
  ctx.lineTo(8 * S, 18 * S);
  ctx.closePath();
  ctx.fillStyle = bird.secondary; ctx.fill();
  ctx.beginPath();
  ctx.ellipse(0, 6 * S, 22 * S, 18 * S, 0, 0, Math.PI * 2);
  ctx.fillStyle = bird.primary; ctx.fill();
  ctx.beginPath();
  ctx.ellipse(3 * S, -13 * S, 14 * S, 13 * S, 0.1, 0, Math.PI * 2);
  ctx.fillStyle = bird.primary; ctx.fill();
  if (bird.crest) {
    const crWave = Math.sin(frame * 0.06) * 1.5 * S;
    ctx.beginPath();
    ctx.moveTo(2 * S, -25 * S + crWave); ctx.lineTo(-4 * S, -21 * S);
    ctx.lineTo(3 * S, -28 * S + crWave); ctx.lineTo(8 * S, -22 * S);
    ctx.lineTo(5 * S, -26 * S); ctx.closePath();
    ctx.fillStyle = bird.secondary; ctx.fill();
  }
  ctx.beginPath();
  ctx.moveTo(13 * S, -15 * S); ctx.lineTo(22 * S, -13 * S); ctx.lineTo(13 * S, -10 * S);
  ctx.closePath(); ctx.fillStyle = bird.beak; ctx.fill();
  ctx.beginPath(); ctx.arc(9 * S, -16 * S, 3.5 * S, 0, Math.PI * 2);
  ctx.fillStyle = bird.eye; ctx.fill();
  ctx.beginPath(); ctx.arc(10 * S, -16 * S, 1.6 * S, 0, Math.PI * 2);
  ctx.fillStyle = '#000'; ctx.fill();
  ctx.beginPath(); ctx.arc(11 * S, -17 * S, 0.7 * S, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.8)'; ctx.fill();
  ctx.beginPath(); ctx.ellipse(-2 * S, -6 * S, 5 * S, 4 * S, -0.4, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.15)'; ctx.fill();
  ctx.strokeStyle = bird.beak; ctx.lineWidth = 2 * S; ctx.lineCap = 'round';
  [[-8, 6], [6, 6]].forEach(([bx]) => {
    const fx = bx * S, fy = 24 * S;
    ctx.beginPath(); ctx.moveTo(fx, fy); ctx.lineTo(fx - 3 * S, fy + 7 * S); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(fx - 3 * S, fy + 7 * S); ctx.lineTo(fx - 7 * S, fy + 11 * S); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(fx - 3 * S, fy + 7 * S); ctx.lineTo(fx, fy + 11 * S); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(fx - 3 * S, fy + 7 * S); ctx.lineTo(fx + 3 * S, fy + 9 * S); ctx.stroke();
  });
  ctx.shadowBlur = 0; ctx.restore();
}

// ─── NFT / Enemy generation helpers ────────────────────────────────────────

export function generateNFTsForWallet(wallet: string) {
  const seed = wallet.toLowerCase().split('').reduce((a: number, c: string) => (a * 31 + c.charCodeAt(0)) | 0, 0);
  const count = 3 + (Math.abs(seed) % 5);
  return Array.from({ length: count }, (_, i) => {
    const s = (seed * 1000003 + i * 999983) | 0;
    const id = 1000 + (Math.abs(s * 17 + i * 9999) % 9000);
    const typeIdx = Math.abs(s * 3 + i * 7) % BIRD_TYPES.length;
    const bird = BIRD_TYPES[typeIdx];
    return {
      id, typeIdx,
      name: `${bird.name} #${id}`,
      type: bird.name,
      atk: 28 + Math.abs((s + i * 7) % 52),
      def: 12 + Math.abs((s + i * 11) % 38),
      spd: 18 + Math.abs((s + i * 17) % 42),
      hp:  75 + Math.abs((s + i * 13) % 75),
      traits: [
        ['Ferocious','Calm','Wise','Wild','Ancient','Cursed','Blessed'][Math.abs(s + i) % 7],
        ['Arena Master','Rookie','Veteran','Legend','Challenger'][i % 5],
      ],
    };
  });
}

export function generateEnemies() {
  const seeds = [0x1a2b3c, 0xdeadbe, 0xcafe01, 0xf00d02, 0x123456, 0xabcde0];
  return seeds.map((seed, i) => {
    const typeIdx = (seed + i * 17) % BIRD_TYPES.length;
    const bird = BIRD_TYPES[typeIdx];
    const id = 5000 + ((seed * 13 + i * 991) % 4000);
    return {
      id, typeIdx,
      name: `${bird.name} #${id}`,
      type: bird.name,
      atk: 28 + ((seed + i * 9) % 55),
      def: 12 + ((seed + i * 15) % 40),
      spd: 18 + ((seed + i * 23) % 45),
      hp:  75 + ((seed + i * 11) % 80),
      traits: ['Fierce', 'Cunning'] as string[],
      isEnemy: true,
      rating: 800 + ((seed * 7 + i * 50) % 400),
    };
  });
}

// ─── Types ────────────────────────────────────────────────────────────────────
interface NftType {
  id: number;
  typeIdx: number;
  name: string;
  type: string;
  atk: number;
  def: number;
  spd: number;
  hp: number;
  traits: (string | undefined)[];
  isEnemy?: boolean;
  rating?: number;
}

interface DmgPopup {
  id: number;
  target: string;
  dmg: number;
  crit: boolean;
  x: number;
  y: number;
}

interface LogEntry {
  msg: string;
  type: string;
  id: number;
}

interface ResultOverlay {
  won: boolean;
  xp: number;
  gold: number;
  streak: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const CONTRACT = '0x3a9454c1b4c84d1861bb1209a647c834d137b442';

const LEADERBOARD = [
  { addr: '0xBird...f7a2', name: 'Flame Wing #2847',  wins: 47, rating: 1842 },
  { addr: '0xClaw...3d11', name: 'Shadow Hawk #1120', wins: 39, rating: 1755 },
  { addr: '0xWing...8a4c', name: 'Golden Eagle #7331',wins: 31, rating: 1690 },
  { addr: '0xTal0...2bc9', name: 'Storm Crow #4882',  wins: 28, rating: 1623 },
  { addr: '0xBeak...9f5e', name: 'Crimson Jay #3304', wins: 22, rating: 1547 },
  { addr: '0xFeat...1d72', name: 'Sky Raider #6619',  wins: 19, rating: 1489 },
  { addr: '0xCres...4a81', name: 'Sunbird #8847',     wins: 14, rating: 1402 },
  { addr: '0xVoid...7c33', name: 'Void Raven #9128',  wins: 11, rating: 1344 },
];

const ACTIONS = [
  { key: 'strike',     label: 'STRIKE',     icon: '⚔️',  desc: 'Basic attack',       baseDmg: 1.0,  ignoresDef: false, special: null       },
  { key: 'wing-slash', label: 'WING SLASH', icon: '🪶',  desc: 'High damage',        baseDmg: 1.45, ignoresDef: false, special: null       },
  { key: 'talon-dive', label: 'TALON DIVE', icon: '🌪️', desc: 'Ignores defense',    baseDmg: 1.2,  ignoresDef: true,  special: null       },
  { key: 'ultimate',   label: 'ULTIMATE',   icon: '💥',  desc: 'Massive burst (×1)', baseDmg: 2.6,  ignoresDef: false, special: 'ultimate' },
  { key: 'shield',     label: 'SHIELD',     icon: '🛡️', desc: 'Block next hit',     baseDmg: 0,    ignoresDef: false, special: 'shield'   },
  { key: 'screech',    label: 'SCREECH',    icon: '📢',  desc: 'Deals dmg + stun',   baseDmg: 0.5,  ignoresDef: false, special: 'stun'     },
  { key: 'heal',       label: 'HEAL',       icon: '💚',  desc: 'Restore 20 HP',      baseDmg: 0,    ignoresDef: false, special: 'heal'     },
  { key: 'rage',       label: 'RAGE',       icon: '🔥',  desc: '+50% ATK, 2 turns',  baseDmg: 0,    ignoresDef: false, special: 'rage'     },
];

// ─── Battle logic ─────────────────────────────────────────────────────────────
function calcDamage(atk: number, def: number, mult: number, ignoresDef: boolean) {
  const effDef = ignoresDef ? 0 : def;
  const base   = Math.max(5, atk * mult - effDef * 0.5);
  const crit   = Math.random() < 0.15;
  const dmg    = Math.round(base * (crit ? 1.8 : 1) * (0.85 + Math.random() * 0.3));
  return { dmg, crit };
}

// ─── BirdImage ────────────────────────────────────────────────────────────────
const BirdImage = React.memo(function BirdImage({
  nft, flipped = false, glowColor = null, className = '',
}: {
  nft: NftType;
  flipped?: boolean;
  glowColor?: string | null;
  className?: string;
}) {
  const bird  = BIRD_TYPES[nft.typeIdx % BIRD_TYPES.length];
  const glow  = glowColor || bird.glow || '#ff6b1a';
  const imgStyle: CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
    transform: flipped ? 'scaleX(-1)' : 'none',
    filter: `drop-shadow(0 0 14px ${glow}99)`,
    animation: 'sa-birdFloat 3s ease-in-out infinite',
    imageRendering: 'pixelated',
    userSelect: 'none',
    pointerEvents: 'none',
  };
  return (
    <img
      src={bird.image}
      alt={nft.name}
      style={imgStyle}
      className={className}
      draggable={false}
    />
  );
});

// ─── HpBar ────────────────────────────────────────────────────────────────────
function HpBar({ current, max, color }: { current: number; max: number; color: string }) {
  const pct = Math.max(0, (current / max) * 100);
  const bar = pct > 60 ? color : pct > 30 ? '#ffd700' : '#ff3d6e';
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', fontSize: 11,
        fontFamily: "'Share Tech Mono',monospace", marginBottom: 3,
        color: 'rgba(200,220,255,0.7)',
      }}>
        <span>HP</span>
        <span style={{ color }}>{current} / {max}</span>
      </div>
      <div style={{
        height: 10, background: 'rgba(0,0,0,0.5)', borderRadius: 5,
        border: '1px solid rgba(100,150,200,0.15)', overflow: 'hidden',
      }}>
        <div style={{
          height: '100%', width: `${pct}%`, borderRadius: 5,
          background: `linear-gradient(90deg, ${bar}, ${bar}cc)`,
          transition: 'width 0.45s ease',
          boxShadow: `0 0 8px ${bar}88`,
        }} />
      </div>
    </div>
  );
}

// ─── StatChip ─────────────────────────────────────────────────────────────────
function StatChip({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{
      textAlign: 'center', background: 'rgba(0,0,0,0.3)',
      border: '1px solid rgba(100,150,200,0.1)', borderRadius: 6, padding: '6px 4px',
    }}>
      <div style={{ fontFamily: "'Bebas Neue',cursive", fontSize: 22, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: 9, color: 'rgba(150,180,220,0.55)', letterSpacing: 1 }}>{label}</div>
    </div>
  );
}

// ─── NftCard ──────────────────────────────────────────────────────────────────
function NftCard({ nft, selected, onClick }: { nft: NftType; selected: boolean; onClick: () => void }) {
  const bird = BIRD_TYPES[nft.typeIdx % BIRD_TYPES.length];
  return (
    <div onClick={onClick} style={{
      background: 'rgba(10,15,28,0.9)',
      border: `1px solid ${selected ? '#ffd700' : 'rgba(0,200,255,0.12)'}`,
      borderRadius: 12, overflow: 'hidden', cursor: 'pointer',
      transform: selected ? 'translateY(-4px)' : 'none',
      boxShadow: selected ? `0 0 28px ${bird.glow}66` : '0 2px 12px rgba(0,0,0,0.4)',
      transition: 'all 0.22s ease', position: 'relative',
    }}>
      {selected && (
        <div style={{
          position: 'absolute', top: 8, right: 8, zIndex: 2,
          background: '#ffd700', color: '#000', fontSize: 9,
          fontFamily: "'Bebas Neue',cursive", letterSpacing: 1,
          padding: '2px 7px', borderRadius: 4,
        }}>✓ SELECTED</div>
      )}
      {/* Bird image panel */}
      <div style={{
        aspectRatio: '1/1',
        background: `radial-gradient(circle at 50% 60%, ${bird.primary}22 0%, #050810 70%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 12,
      }}>
        <BirdImage nft={nft} />
      </div>
      <div style={{ padding: '10px 12px', borderTop: '1px solid rgba(0,200,255,0.08)' }}>
        <div style={{ fontFamily: "'Bebas Neue',cursive", fontSize: 15, letterSpacing: 2, color: '#e0e8ff', marginBottom: 5 }}>{nft.name}</div>
        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginBottom: 7 }}>
          {nft.traits.map(t => (
            <span key={t} style={{
              fontSize: 9, fontFamily: "'Share Tech Mono',monospace",
              background: 'rgba(255,107,26,0.1)', border: '1px solid rgba(255,107,26,0.2)',
              color: '#ff6b1a', padding: '1px 5px', borderRadius: 3,
            }}>{t}</span>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
          <StatChip label="ATK" value={nft.atk} color="#ff6b1a" />
          <StatChip label="DEF" value={nft.def} color="#00c8ff" />
          <StatChip label="SPD" value={nft.spd} color="#00ff88" />
        </div>
      </div>
    </div>
  );
}

// ─── FighterPanel ─────────────────────────────────────────────────────────────
function FighterPanel({ nft, hp, maxHp, side, anim, statuses, glowColor, dmgPopups }: {
  nft: NftType;
  hp: number;
  maxHp: number;
  side: string;
  anim: string;
  statuses: string[];
  glowColor: string | null;
  dmgPopups: DmgPopup[];
}) {
  const isPlayer = side === 'player';
  const bird     = BIRD_TYPES[nft.typeIdx % BIRD_TYPES.length];

  // CSS animation class for the wrapper div
  const animClass =
    anim === 'attack' ? (isPlayer ? 'sa-attack-r' : 'sa-attack-l')
    : anim === 'hit'  ? 'sa-hit'
    : anim === 'dead' ? 'sa-dead'
    : '';

  return (
    <div style={{
      background: 'rgba(10,15,28,0.88)',
      border: `1px solid ${isPlayer ? 'rgba(0,200,255,0.22)' : 'rgba(255,107,26,0.22)'}`,
      borderRadius: 14, padding: '12px 12px',
      backdropFilter: 'blur(12px)',
    }}>
      <div style={{
        fontFamily: "'Bebas Neue',cursive", fontSize: 18, letterSpacing: 2, marginBottom: 8,
        color: isPlayer ? '#00c8ff' : '#ff6b1a',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{nft.name}</div>

      <HpBar current={hp} max={maxHp} color={isPlayer ? '#00c8ff' : '#ff6b1a'} />

      {/* Status badges */}
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', minHeight: 22, marginBottom: 6 }}>
        {statuses.map(s => (
          <span key={s} style={{
            fontSize: 9, fontFamily: "'Share Tech Mono',monospace",
            padding: '2px 7px', borderRadius: 10,
            animation: 'sa-pulse 1.5s infinite',
            ...(s === 'SHIELD'  ? { background: 'rgba(0,200,255,0.12)',  border: '1px solid rgba(0,200,255,0.4)',  color: '#00c8ff' }
              : s === 'RAGE'    ? { background: 'rgba(255,0,60,0.12)',   border: '1px solid rgba(255,0,60,0.4)',   color: '#ff3d6e' }
              : s === 'STUNNED' ? { background: 'rgba(255,215,0,0.12)',  border: '1px solid rgba(255,215,0,0.4)',  color: '#ffd700' }
              : {}),
          }}>{s}</span>
        ))}
      </div>

      {/* Bird image + damage popups */}
      <div className={animClass} style={{
        width: '100%', aspectRatio: '1/1', maxHeight: 200,
        borderRadius: 10, position: 'relative', overflow: 'visible',
        background: `radial-gradient(circle at 50% 55%, ${bird.primary}18 0%, transparent 70%)`,
      }}>
        {/* BirdImage is memoised — dmgPopup updates don't re-render it */}
        <BirdImage
          nft={nft}
          flipped={!isPlayer}
          glowColor={glowColor}
        />

        {/* Damage popups — positions stored at creation time (no render-time Math.random) */}
        {dmgPopups.map(p => (
          <div key={p.id} style={{
            position: 'absolute',
            left: `${p.x}%`,
            top: `${p.y}%`,
            pointerEvents: 'none', zIndex: 50,
            fontFamily: "'Bebas Neue',cursive", fontSize: 26,
            color: p.crit ? '#ff3d6e' : '#fff',
            textShadow: `0 0 8px ${p.crit ? '#ff3d6e' : '#ff6b1a'}, 2px 2px 0 #000`,
            animation: 'sa-dmgFloat 1.2s ease forwards',
          }}>
            {p.crit ? '💥 ' : ''}{p.dmg}
          </div>
        ))}
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 5, marginTop: 8 }}>
        <StatChip label="ATK" value={nft.atk} color={isPlayer ? '#00c8ff' : '#ff6b1a'} />
        <StatChip label="DEF" value={nft.def} color={isPlayer ? '#00c8ff' : '#ff6b1a'} />
        <StatChip label="SPD" value={nft.spd} color={isPlayer ? '#00c8ff' : '#ff6b1a'} />
      </div>
    </div>
  );
}

// ─── StarfieldCanvas ─────────────────────────────────────────────────────────
function StarfieldCanvas() {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current!;
    const ctx = c.getContext('2d')!;
    let stars: { x: number; y: number; r: number; speed: number; phase: number }[] = [];
    let raf: number;
    const resize = () => {
      c.width  = window.innerWidth;
      c.height = window.innerHeight;
      stars = Array.from({ length: 130 }, () => ({
        x: Math.random() * c.width, y: Math.random() * c.height,
        r: Math.random() * 1.4 + 0.2,
        speed: Math.random() * 0.3 + 0.08,
        phase: Math.random() * Math.PI * 2,
      }));
    };
    const draw = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      const t = Date.now() / 1000;
      stars.forEach(s => {
        const a = 0.25 + 0.4 * Math.sin(t * s.speed + s.phase);
        ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(200,220,255,${a})`; ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    window.addEventListener('resize', resize);
    resize(); draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);
  return (
    <canvas ref={ref} style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      pointerEvents: 'none', zIndex: 0,
    }} />
  );
}

// ─── SquadArena ───────────────────────────────────────────────────────────────
export default function SquadArena() {
  const [page, setPage]         = useState('connect');
  const [tab, setTab]           = useState('my-birds');
  const [walletInput, setWalletInput]   = useState('0xe12d417f8b00eb726c545f26772b48c1bd49bc6b');
  const [wallet, setWallet]             = useState<string | null>(null);
  const [nfts, setNfts]                 = useState<NftType[]>([]);
  const [selectedNft, setSelectedNft]   = useState<NftType | null>(null);
  const [enemies, setEnemies]           = useState<NftType[]>([]);
  const [enemyNft, setEnemyNft]         = useState<NftType | null>(null);
  const [verifying, setVerifying]       = useState(false);
  const [connectError, setConnectError] = useState('');
  const [playerHp, setPlayerHp]               = useState(100);
  const [playerMaxHp, setPlayerMaxHp]         = useState(100);
  const [enemyHp, setEnemyHp]                 = useState(100);
  const [enemyMaxHp, setEnemyMaxHp]           = useState(100);
  const [round, setRound]                     = useState(1);
  const [turn, setTurn]                       = useState('player');
  const [battleLog, setBattleLog]             = useState<LogEntry[]>([]);
  const [actionsDisabled, setActionsDisabled] = useState(false);
  const [turnBusy, setTurnBusy]               = useState(false);
  const [resultOverlay, setResultOverlay]     = useState<ResultOverlay | null>(null);
  const [winStreak, setWinStreak]             = useState(0);
  const [shieldActive, setShieldActive]       = useState(false);
  const [rageActive, setRageActive]           = useState(false);
  const [rageTurns, setRageTurns]             = useState(0);
  const [enemyStunned, setEnemyStunned]       = useState(false);
  const [ultimateUsed, setUltimateUsed]       = useState(false);
  const [playerStatuses, setPlayerStatuses]   = useState<string[]>([]);
  const [enemyStatuses, setEnemyStatuses]     = useState<string[]>([]);
  const [dmgPopups, setDmgPopups]             = useState<DmgPopup[]>([]);
  const [playerAnim, setPlayerAnim]           = useState('idle');
  const [enemyAnim, setEnemyAnim]             = useState('idle');

  // Hero canvas (connect page only — canvas still used here as decorative art)
  const heroCanvasRef = useRef<HTMLCanvasElement>(null);
  const heroFrameRef  = useRef(0);
  const heroRafRef    = useRef<number | null>(null);

  // All async callbacks read from battleRef — never stale
  const battleRef = useRef<{
    playerHp?: number; enemyHp?: number; playerMaxHp?: number; enemyMaxHp?: number;
    shieldActive?: boolean; rageActive?: boolean; rageTurns?: number;
    enemyStunned?: boolean; ultimateUsed?: boolean;
    selectedNft?: NftType | null; enemyNft?: NftType | null;
    round?: number; winStreak?: number;
  }>({});
  useEffect(() => {
    battleRef.current = {
      playerHp, enemyHp, playerMaxHp, enemyMaxHp,
      shieldActive, rageActive, rageTurns, enemyStunned, ultimateUsed,
      selectedNft, enemyNft, round, winStreak,
    };
  });

  // Hero canvas animation
  useEffect(() => {
    if (page !== 'connect') {
      if (heroRafRef.current !== null) cancelAnimationFrame(heroRafRef.current);
      return;
    }
    const loop = () => {
      heroFrameRef.current++;
      if (heroCanvasRef.current)
        drawBird(heroCanvasRef.current, 0, heroFrameRef.current, false, '#ff6b1a');
      heroRafRef.current = requestAnimationFrame(loop);
    };
    heroRafRef.current = requestAnimationFrame(loop);
    return () => { if (heroRafRef.current !== null) cancelAnimationFrame(heroRafRef.current); };
  }, [page]);

  const logRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [battleLog]);

  const addLog = useCallback((msg: string, type = 'system') => {
    setBattleLog(prev => [...prev.slice(-40), { msg, type, id: Date.now() + Math.random() }]);
  }, []);

  const addPopup = useCallback((target: string, dmg: number, crit: boolean) => {
    const id = Date.now() + Math.random();
    const x  = 20 + Math.random() * 55;
    const y  = 5  + Math.random() * 25;
    setDmgPopups(prev => [...prev, { id, target, dmg, crit, x, y }]);
    setTimeout(() => setDmgPopups(prev => prev.filter(p => p.id !== id)), 1300);
  }, []);

  const flashAnim = useCallback((who: string, anim: string) => {
    const set = who === 'player' ? setPlayerAnim : setEnemyAnim;
    set(anim);
    setTimeout(() => set('idle'), 600);
  }, []);

  const addStatus = useCallback((target: string, s: string) => {
    const fn = target === 'player' ? setPlayerStatuses : setEnemyStatuses;
    fn(prev => prev.includes(s) ? prev : [...prev, s]);
  }, []);

  const dropStatus = useCallback((target: string, s: string) => {
    const fn = target === 'player' ? setPlayerStatuses : setEnemyStatuses;
    fn(prev => prev.filter(x => x !== s));
  }, []);

  const verifyWallet = useCallback(async () => {
    setConnectError('');
    const addr = walletInput.trim();
    if (!/^0x[a-fA-F0-9]{40}$/.test(addr)) {
      setConnectError('⚠ Invalid address. Must be a valid 0x Monad/EVM address (42 chars).');
      return;
    }
    setVerifying(true);
    await new Promise(r => setTimeout(r, 1100));
    const generated = generateNFTsForWallet(addr);
    if (!generated.length) {
      setConnectError('⚠ No 10k Squad NFTs found in this wallet.');
      setVerifying(false);
      return;
    }
    setWallet(addr);
    setNfts(generated);
    setEnemies(generateEnemies());
    setVerifying(false);
    setPage('select');
  }, [walletInput]);

  // endBattle declared BEFORE doAction / runEnemyTurn (fixes stale closure)
  const endBattle = useCallback((won: boolean) => {
    setActionsDisabled(true);
    setTurnBusy(true);
    const xp     = won ? 50 + Math.floor(Math.random() * 50) : 10;
    const gold   = won ? 20 + Math.floor(Math.random() * 30) : 5;
    const streak = won ? (battleRef.current.winStreak ?? 0) + 1 : 0;
    setWinStreak(streak);
    addLog(
      won ? `🏆 VICTORY! ${battleRef.current.selectedNft?.name} wins!` : '💀 DEFEAT. Rise again!',
      won ? 'win' : 'enemy',
    );
    if (won) flashAnim('enemy', 'dead'); else flashAnim('player', 'dead');
    setTimeout(() => setResultOverlay({ won, xp, gold, streak }), 900);
  }, [addLog, flashAnim]);

  const runEnemyTurn = useCallback(() => {
    const {
      playerHp: pHp, enemyHp: eHp, enemyMaxHp: eMaxHp,
      shieldActive: shield, enemyStunned: stunned,
      selectedNft: p, enemyNft: e,
    } = battleRef.current;

    if (stunned) {
      setEnemyStunned(false);
      dropStatus('enemy', 'STUNNED');
      addLog(`⚡ ${e!.name} is stunned — skips turn!`, 'enemy');
      setTurn('player');
      setActionsDisabled(false);
      setTurnBusy(false);
      return;
    }

    const hpPct = (eHp ?? 0) / (eMaxHp ?? 1);
    const rng   = Math.random();
    let choice: string;
    if (hpPct < 0.25 && rng < 0.4)  choice = 'heal';
    else if (rng < 0.30)             choice = 'strike';
    else if (rng < 0.55)             choice = 'wing-slash';
    else if (rng < 0.72)             choice = 'talon-dive';
    else                             choice = 'screech';

    const action = ACTIONS.find(a => a.key === choice)!;

    if (action.special === 'heal') {
      const healed = Math.min(20, (eMaxHp ?? 0) - (eHp ?? 0));
      setEnemyHp(prev => Math.min(battleRef.current.enemyMaxHp ?? prev, prev + 20));
      addLog(`💊 ${e!.name} heals for ${healed} HP!`, 'enemy');
    } else {
      const { dmg, crit } = calcDamage(e!.atk, p!.def, action.baseDmg, action.ignoresDef || false);
      if (shield) {
        setShieldActive(false);
        dropStatus('player', 'SHIELD');
        addLog(`🛡 ${p!.name}'s SHIELD absorbs ${e!.name}'s ${action.label}!`, 'player');
      } else {
        setPlayerHp(prev => Math.max(0, prev - dmg));
        addLog(`${action.label}${crit ? ' ✨CRIT' : ''}: ${e!.name} deals ${dmg} dmg!`, crit ? 'crit' : 'enemy');
        addPopup('player', dmg, crit);
        flashAnim('enemy', 'attack');
        flashAnim('player', 'hit');
      }
    }

    setTimeout(() => {
      if ((battleRef.current.playerHp ?? 1) <= 0) {
        endBattle(false);
      } else {
        setRound(r => r + 1);
        setTurn('player');
        setActionsDisabled(false);
        setTurnBusy(false);
      }
    }, 50);
  }, [addLog, addPopup, flashAnim, dropStatus, endBattle]);

  const doAction = useCallback((actionKey: string) => {
    if (actionsDisabled || turnBusy) return;
    const action = ACTIONS.find(a => a.key === actionKey)!;
    const {
      selectedNft: p, enemyNft: e,
      rageActive: rage, rageTurns: rTurns,
      ultimateUsed: uUsed,
      playerMaxHp: pMaxHp, playerHp: pHp,
    } = battleRef.current;

    if (action.special === 'ultimate' && uUsed) {
      addLog('Ultimate already used this battle!', 'system');
      return;
    }

    setActionsDisabled(true);
    setTurnBusy(true);
    const atkMult = rage ? 1.5 : 1.0;

    switch (action.special) {
      case 'shield':
        setShieldActive(true);
        addStatus('player', 'SHIELD');
        addLog(`🛡 ${p!.name} raises a shield!`, 'player');
        break;

      case 'heal': {
        const healed = Math.min(20, (pMaxHp ?? 0) - (pHp ?? 0));
        setPlayerHp(prev => Math.min(pMaxHp ?? prev, prev + 20));
        addLog(`💚 ${p!.name} heals for ${healed} HP!`, 'player');
        break;
      }

      case 'rage':
        setRageActive(true);
        setRageTurns(2);
        addStatus('player', 'RAGE');
        addLog(`🔥 ${p!.name} enters RAGE MODE! ATK ×1.5 for 2 turns!`, 'player');
        break;

      case 'ultimate': {
        setUltimateUsed(true);
        const { dmg, crit } = calcDamage(p!.atk * atkMult, e!.def, action.baseDmg, action.ignoresDef);
        setEnemyHp(prev => Math.max(0, prev - dmg));
        addLog(`💥 ULTIMATE${crit ? ' ✨CRIT' : ''}: ${p!.name} deals ${dmg} dmg!`, crit ? 'crit' : 'player');
        addPopup('enemy', dmg, crit);
        flashAnim('player', 'attack');
        flashAnim('enemy', 'hit');
        break;
      }

      default: {
        const { dmg, crit } = calcDamage(p!.atk * atkMult, e!.def, action.baseDmg, action.ignoresDef);
        if (action.special === 'stun') {
          setEnemyStunned(true);
          addStatus('enemy', 'STUNNED');
          addLog(`📢 ${p!.name} SCREECHES${crit ? ' ✨CRIT' : ''} — ${dmg} dmg + STUN!`, crit ? 'crit' : 'player');
        } else {
          addLog(`${action.label}${crit ? ' ✨CRIT' : ''}: ${p!.name} deals ${dmg} dmg!`, crit ? 'crit' : 'player');
        }
        setEnemyHp(prev => Math.max(0, prev - dmg));
        addPopup('enemy', dmg, crit);
        flashAnim('player', 'attack');
        flashAnim('enemy', 'hit');
        break;
      }
    }

    if (rage && action.special !== 'rage') {
      const newRage = (rTurns ?? 0) - 1;
      setRageTurns(newRage);
      if (newRage <= 0) {
        setRageActive(false);
        dropStatus('player', 'RAGE');
        addLog('🔥 Rage fades.', 'system');
      }
    }

    setTimeout(() => {
      if ((battleRef.current.enemyHp ?? 1) <= 0) {
        endBattle(true);
      } else {
        setRound(r => r + 1);
        setTurn('enemy');
        setTimeout(runEnemyTurn, 900);
      }
    }, 50);
  }, [actionsDisabled, turnBusy, addLog, addPopup, addStatus, dropStatus, flashAnim, runEnemyTurn, endBattle]);

  const startBattle = useCallback((enemy?: NftType) => {
    if (!selectedNft) return;
    const opp = enemy || generateEnemies()[0];
    setEnemyNft(opp);
    setPlayerHp(selectedNft.hp);    setPlayerMaxHp(selectedNft.hp);
    setEnemyHp(opp.hp);             setEnemyMaxHp(opp.hp);
    setRound(1);
    setTurn(selectedNft.spd >= opp.spd ? 'player' : 'enemy');
    setBattleLog([]);
    setActionsDisabled(false);
    setTurnBusy(false);
    setResultOverlay(null);
    setShieldActive(false);  setRageActive(false);  setRageTurns(0);
    setEnemyStunned(false);  setUltimateUsed(false);
    setPlayerStatuses([]);   setEnemyStatuses([]);
    setDmgPopups([]);
    setPlayerAnim('idle');   setEnemyAnim('idle');
    setPage('arena');
    setTimeout(() => addLog(`⚔ BATTLE: ${selectedNft.name}  vs  ${opp.name}`, 'system'), 50);
    setTimeout(() => {
      const first = selectedNft.spd >= opp.spd ? selectedNft.name : opp.name;
      addLog(`${first} moves first (higher SPD)`, 'system');
    }, 100);
  }, [selectedNft, addLog]);

  const rematch = useCallback(() => {
    setResultOverlay(null);
    setTimeout(() => startBattle(battleRef.current.enemyNft ?? undefined), 50);
  }, [startBattle]);

  const disconnect = useCallback(() => {
    setWallet(null); setNfts([]); setSelectedNft(null); setEnemyNft(null);
    setPage('connect');
  }, []);

  const S = styles;

  return (
    <div style={S.root}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Rajdhani:wght@400;500;600;700&family=Share+Tech+Mono&display=swap');

        @keyframes sa-pulse     { 0%,100%{opacity:1} 50%{opacity:0.45} }
        @keyframes sa-vsFloat   { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(-6px) scale(1.06)} }
        @keyframes sa-fadeSlide { from{opacity:0;transform:translateX(-8px)} to{opacity:1;transform:none} }
        @keyframes sa-cardIn    { from{opacity:0;transform:scale(0.72)} to{opacity:1;transform:scale(1)} }
        @keyframes sa-spin      { to{transform:rotate(360deg)} }
        @keyframes sa-dmgFloat  { 0%{opacity:1;transform:translateY(0) scale(1)} 30%{opacity:1;transform:translateY(-28px) scale(1.25)} 100%{opacity:0;transform:translateY(-75px) scale(0.8)} }

        /* Bird idle float — applied directly to <img> */
        @keyframes sa-birdFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        /* Flipped bird floats too (transform composed) */
        @keyframes sa-birdFloatFlip { 0%,100%{transform:scaleX(-1) translateY(0)} 50%{transform:scaleX(-1) translateY(-8px)} }

        /* Fighter wrapper animations */
        @keyframes sa-hitShake  { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-8px)} 40%{transform:translateX(8px)} 60%{transform:translateX(-5px)} 80%{transform:translateX(5px)} }
        @keyframes sa-attackR   { 0%{transform:translateX(0)} 30%{transform:translateX(22px) scale(1.08)} 100%{transform:translateX(0)} }
        @keyframes sa-attackL   { 0%{transform:translateX(0)} 30%{transform:translateX(-22px) scale(1.08)} 100%{transform:translateX(0)} }
        @keyframes sa-deadFall  { 0%{transform:rotate(0) scale(1);opacity:1} 100%{transform:rotate(75deg) scale(0.55);opacity:0} }

        .sa-hit      { animation: sa-hitShake 0.45s ease; }
        .sa-attack-r { animation: sa-attackR  0.5s  ease; }
        .sa-attack-l { animation: sa-attackL  0.5s  ease; }
        .sa-dead     { animation: sa-deadFall 1s    ease forwards; }

        .sa-btn:hover  { filter:brightness(1.13); transform:translateY(-2px) !important; }
        .sa-btn:active { transform:translateY(0) !important; filter:brightness(0.95); }

        .sa-nft-card:hover  { transform:translateY(-4px) !important; box-shadow:0 6px 24px rgba(255,107,26,0.3) !important; border-color:#ff6b1a !important; }
        .sa-enemy-card:hover { border-color:#ff3d6e !important; transform:translateY(-3px); box-shadow:0 0 18px rgba(255,60,110,0.3) !important; }

        .sa-action:hover:not(:disabled) { border-color:#ff6b1a !important; background:rgba(255,107,26,0.14) !important; transform:translateY(-2px); box-shadow:0 0 18px rgba(255,107,26,0.35); }
        .sa-action:disabled { opacity:0.35; cursor:not-allowed; }
        .sa-action.sa-special:hover:not(:disabled) { border-color:#ffd700 !important; box-shadow:0 0 22px rgba(255,215,0,0.35) !important; }

        .sa-tab.active { background:rgba(0,200,255,0.1); color:#00c8ff; border:1px solid rgba(0,200,255,0.22); }
        .sa-tab:hover:not(.active) { background:rgba(0,200,255,0.05); color:rgba(200,220,255,0.8); }
        .sa-lb-row:hover { background:rgba(0,200,255,0.06); }

        .sa-wallet-input:focus { border-color:#00c8ff !important; box-shadow:0 0 0 2px rgba(0,200,255,0.15); outline:none; }

        /* Responsive */
        @media (max-width:640px) {
          .sa-arena-grid   { grid-template-columns:1fr !important; }
          .sa-vs-row       { flex-direction:row !important; padding-top:0 !important; gap:14px; justify-content:center; }
          .sa-action-grid  { grid-template-columns:repeat(2,1fr) !important; }
          .sa-nft-grid     { grid-template-columns:repeat(2,1fr) !important; }
          .sa-arena-bar    { flex-direction:column; align-items:flex-start !important; }
        }
        @media (max-width:360px) {
          .sa-nft-grid { grid-template-columns:1fr !important; }
        }

        ::-webkit-scrollbar { width:4px; }
        ::-webkit-scrollbar-track { background:transparent; }
        ::-webkit-scrollbar-thumb { background:rgba(0,200,255,0.2); border-radius:2px; }
      `}</style>

      <StarfieldCanvas />
      <div style={S.gridOverlay} />

      <div style={S.inner}>
        {/* HEADER */}
        <header style={S.header}>
          <div style={S.logoMain}>10K SQUAD</div>
          <div style={S.logoSub}>⚔ PVP BATTLE ARENA ⚔</div>
          <div style={S.headerLine} />
        </header>

        {/* ════════ CONNECT ════════ */}
        {page === 'connect' && (
          <div style={S.centeredPage}>
            <div style={S.connectCard}>
              {/* Hero — still uses canvas for the connect page decorative bird */}
              <div style={S.heroWrap}>
                <canvas ref={heroCanvasRef} width={130} height={130} style={{ width: '100%', height: '100%' }} />
              </div>
              <div style={S.chainBadge}>
                <div style={{ ...S.dot, animation: 'sa-pulse 1.5s infinite', background: '#a855f7' }} />
                MONAD MAINNET
              </div>
              <div style={S.connectTitle}>ENTER THE ARENA</div>
              <p style={S.connectDesc}>
                Verify your 10k Squad NFT ownership to bring your bird to life and battle for glory on-chain.
              </p>
              <input
                className="sa-wallet-input"
                style={S.walletInput}
                value={walletInput}
                onChange={e => setWalletInput(e.target.value)}
                placeholder="0x… enter your Monad wallet address"
                autoComplete="off" spellCheck={false}
                onKeyDown={e => e.key === 'Enter' && verifyWallet()}
              />
              {connectError && <div style={S.errorMsg}>{connectError}</div>}
              <button
                className="sa-btn"
                style={{ ...S.btnPrimary, marginTop: 14, width: '100%' }}
                onClick={verifyWallet}
                disabled={verifying}
              >
                {verifying ? (
                  <span style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10 }}>
                    <div style={{ ...S.spinner, animation:'sa-spin 0.7s linear infinite' }} />
                    VERIFYING…
                  </span>
                ) : '⚔ VERIFY & ENTER'}
              </button>
              <div style={S.contractNote}>CONTRACT: {CONTRACT}</div>
            </div>
          </div>
        )}

        {/* ════════ SELECT ════════ */}
        {page === 'select' && (
          <div style={S.selectPage}>
            {/* Wallet bar */}
            <div style={S.walletBar}>
              <div style={{ ...S.dot, background: '#00ff88', animation: 'sa-pulse 1.5s infinite', flexShrink: 0 }} />
              <span style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:13, color:'#00c8ff', flex:1, overflow:'hidden', textOverflow:'ellipsis' }}>
                {wallet?.slice(0,8)}…{wallet?.slice(-6)}
              </span>
              <span style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:11, color:'#00ff88' }}>
                {nfts.length} NFT{nfts.length !== 1 ? 's' : ''} FOUND
              </span>
              <button className="sa-btn" style={S.btnSmall} onClick={disconnect}>DISCONNECT</button>
            </div>

            {/* Tabs */}
            <div style={S.tabBar}>
              {['my-birds','enemies','leaderboard'].map(t => (
                <button
                  key={t}
                  className={`sa-tab${tab === t ? ' active' : ''}`}
                  style={S.tab}
                  onClick={() => setTab(t)}
                >
                  {t === 'my-birds' ? 'MY BIRDS' : t === 'enemies' ? 'OPPONENT' : 'SCORES'}
                </button>
              ))}
            </div>

            {/* MY BIRDS */}
            {tab === 'my-birds' && (
              <>
                <div style={S.sectionTitle}>SELECT YOUR FIGHTER</div>
                <div className="sa-nft-grid" style={S.nftGrid}>
                  {nfts.map(nft => (
                    <div key={nft.id} className="sa-nft-card" style={{ transition: 'all 0.22s' }}>
                      <NftCard
                        nft={nft}
                        selected={selectedNft?.id === nft.id}
                        onClick={() => setSelectedNft(nft)}
                      />
                    </div>
                  ))}
                </div>
                <div style={{ textAlign:'center', marginTop:8 }}>
                  <button
                    className="sa-btn"
                    style={{ ...S.btnPrimary, maxWidth:340, width:'100%', opacity: selectedNft ? 1 : 0.45 }}
                    disabled={!selectedNft}
                    onClick={() => { setEnemyNft(null); startBattle(); }}
                  >
                    {selectedNft ? `⚔ FIGHT WITH ${selectedNft.name.toUpperCase()}` : '⚔ SELECT A BIRD FIRST'}
                  </button>
                </div>
              </>
            )}

            {/* ENEMIES */}
            {tab === 'enemies' && (
              <>
                <div style={S.sectionTitle}>CHOOSE YOUR OPPONENT</div>
                {!selectedNft && (
                  <div style={{ ...S.errorMsg, display:'block', marginBottom:14 }}>
                    ⚠ Select YOUR bird from "MY BIRDS" first, then challenge an opponent.
                  </div>
                )}
                <div className="sa-nft-grid" style={S.nftGrid}>
                  {enemies.map(en => {
                    const eBird = BIRD_TYPES[en.typeIdx % BIRD_TYPES.length];
                    return (
                      <div
                        key={en.id}
                        className="sa-enemy-card"
                        style={{
                          background: 'rgba(10,15,28,0.9)',
                          border: '1px solid rgba(255,107,26,0.18)',
                          borderRadius: 12, overflow: 'hidden',
                          cursor: selectedNft ? 'pointer' : 'not-allowed',
                          opacity: selectedNft ? 1 : 0.6,
                          transition: 'all 0.22s', position: 'relative',
                        }}
                        onClick={() => selectedNft && startBattle(en)}
                      >
                        <div style={{
                          position:'absolute', top:8, left:8, zIndex:2,
                          fontFamily:"'Bebas Neue',cursive", fontSize:12, color:'#ffd700',
                          background:'rgba(0,0,0,0.65)', padding:'1px 6px', borderRadius:4,
                        }}>⚡ {en.rating}</div>
                        <div style={{
                          aspectRatio: '1/1',
                          background: `radial-gradient(circle at 50% 60%, ${eBird.primary}22 0%, #050810 70%)`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          padding: 12,
                        }}>
                          <BirdImage nft={en} flipped />
                        </div>
                        <div style={{ padding:'10px 12px', borderTop:'1px solid rgba(255,107,26,0.1)' }}>
                          <div style={{ fontFamily:"'Bebas Neue',cursive", fontSize:15, letterSpacing:2, color:'#e0e8ff', marginBottom:6 }}>{en.name}</div>
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:4 }}>
                            <StatChip label="ATK" value={en.atk} color="#ff6b1a" />
                            <StatChip label="DEF" value={en.def} color="#00c8ff" />
                            <StatChip label="SPD" value={en.spd} color="#00ff88" />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* LEADERBOARD */}
            {tab === 'leaderboard' && (
              <>
                <div style={S.sectionTitle}>BATTLE LEADERBOARD</div>
                {LEADERBOARD.map((row, i) => (
                  <div key={i} className="sa-lb-row" style={S.lbRow}>
                    <div style={{ fontFamily:"'Bebas Neue',cursive", fontSize:22, color:'#ffd700', width:32 }}>
                      {['🥇','🥈','🥉','4','5','6','7','8'][i]}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:600, fontSize:14, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.name}</div>
                      <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:10, color:'rgba(150,180,220,0.5)' }}>{row.addr}</div>
                    </div>
                    <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:13, color:'#00ff88' }}>{row.wins}W</div>
                    <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:13, color:'#00c8ff', marginLeft:12 }}>{row.rating}</div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* ════════ ARENA ════════ */}
        {page === 'arena' && selectedNft && enemyNft && (
          <div style={S.arenaPage}>
            {/* Top bar */}
            <div className="sa-arena-bar" style={S.arenaBar}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ ...S.dot, background:'#00ff88', animation:'sa-pulse 1.5s infinite' }} />
                <span style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:12, color:'rgba(200,220,255,0.6)' }}>
                  BATTLE IN PROGRESS
                </span>
              </div>
              <div style={{ fontFamily:"'Bebas Neue',cursive", fontSize:22, letterSpacing:3, color:'#e0e8ff' }}>
                ROUND {round}
              </div>
              <button className="sa-btn" style={S.btnSmall} onClick={() => setPage('select')}>← RETREAT</button>
            </div>

            {/* Fighter grid */}
            <div className="sa-arena-grid" style={S.arenaGrid}>
              <FighterPanel
                nft={selectedNft} hp={playerHp} maxHp={playerMaxHp}
                side="player" anim={playerAnim} statuses={playerStatuses}
                glowColor={rageActive ? '#ff3d6e' : shieldActive ? '#00c8ff' : null}
                dmgPopups={dmgPopups.filter(p => p.target === 'player')}
              />

              <div className="sa-vs-row" style={S.vsColumn}>
                <div style={{
                  fontFamily:"'Bebas Neue',cursive", fontSize:48, lineHeight:1,
                  background:'linear-gradient(180deg,#ffd700,#ff6b1a)',
                  WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text',
                  filter:'drop-shadow(0 0 18px rgba(255,215,0,0.5))',
                  animation:'sa-vsFloat 2.2s ease-in-out infinite',
                }}>VS</div>
                <div style={{
                  fontFamily:"'Bebas Neue',cursive", fontSize:12, letterSpacing:3,
                  color: turn === 'player' ? '#00ff88' : '#ff6b1a',
                  transition:'color 0.3s', whiteSpace:'nowrap',
                }}>
                  {turn === 'player' ? '⚡ YOUR TURN' : '⏳ ENEMY'}
                </div>
              </div>

              <FighterPanel
                nft={enemyNft} hp={enemyHp} maxHp={enemyMaxHp}
                side="enemy" anim={enemyAnim} statuses={enemyStatuses}
                glowColor={null}
                dmgPopups={dmgPopups.filter(p => p.target === 'enemy')}
              />
            </div>

            {/* Battle log */}
            <div ref={logRef} style={S.battleLog}>
              {battleLog.map(entry => (
                <div key={entry.id} style={{
                  ...S.logEntry,
                  color: entry.type === 'player' ? '#00c8ff'
                       : entry.type === 'enemy'  ? '#ff8855'
                       : entry.type === 'crit'   ? '#ff3d6e'
                       : entry.type === 'win'    ? '#00ff88'
                       : '#ffd700',
                }}>{entry.msg}</div>
              ))}
            </div>

            {/* Actions */}
            <div className="sa-action-grid" style={S.actionGrid}>
              {ACTIONS.map(action => (
                <button
                  key={action.key}
                  className={`sa-action${action.special === 'ultimate' ? ' sa-special' : ''}`}
                  style={{
                    ...S.actionBtn,
                    ...(action.special === 'ultimate' ? S.actionBtnSpecial : {}),
                    cursor: actionsDisabled ? 'not-allowed' : 'pointer',
                  }}
                  disabled={actionsDisabled || (action.special === 'ultimate' && ultimateUsed)}
                  onClick={() => doAction(action.key)}
                >
                  <span style={{ fontSize:20, display:'block', marginBottom:2 }}>{action.icon}</span>
                  <div style={{ fontFamily:"'Bebas Neue',cursive", fontSize:13, letterSpacing:1.5 }}>{action.label}</div>
                  <div style={{ fontSize:9, color:'rgba(150,180,220,0.55)', marginTop:2, fontFamily:"'Share Tech Mono',monospace" }}>{action.desc}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ════════ RESULT OVERLAY ════════ */}
      {resultOverlay && (
        <div style={S.resultBg}>
          <div style={{ ...S.resultCard, animation:'sa-cardIn 0.4s cubic-bezier(0.34,1.56,0.64,1)' }}>
            <div style={{
              fontFamily:"'Bebas Neue',cursive", fontSize:'clamp(38px,10vw,58px)',
              letterSpacing:5, lineHeight:1, marginBottom:10,
              background: resultOverlay.won
                ? 'linear-gradient(135deg,#00ff88,#00c8ff)'
                : 'linear-gradient(135deg,#ff6b1a,#ff3d6e)',
              WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text',
              filter:`drop-shadow(0 0 20px ${resultOverlay.won ? 'rgba(0,255,136,0.5)' : 'rgba(255,107,26,0.5)'})`,
            }}>
              {resultOverlay.won ? '🏆 VICTORY!' : '💀 DEFEATED'}
            </div>
            <p style={{ color:'rgba(200,220,255,0.7)', fontSize:15, marginBottom:24, lineHeight:1.6 }}>
              {resultOverlay.won
                ? `${selectedNft?.name} proves unstoppable! Win streak: ${resultOverlay.streak}`
                : `${selectedNft?.name} fought with honor. Rise and fight again!`}
            </p>
            <div style={{ display:'flex', justifyContent:'center', gap:14, marginBottom:28, flexWrap:'wrap' }}>
              {[
                { val: resultOverlay.xp,   lbl: 'XP EARNED'  },
                { val: resultOverlay.gold, lbl: '$SQUAD'      },
                ...(resultOverlay.won ? [{ val: resultOverlay.streak, lbl: 'WIN STREAK' }] : []),
              ].map(item => (
                <div key={item.lbl} style={S.rewardItem}>
                  <div style={{ fontFamily:"'Bebas Neue',cursive", fontSize:30, color:'#ffd700', lineHeight:1 }}>{item.val}</div>
                  <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:10, color:'rgba(150,180,220,0.55)', marginTop:2 }}>{item.lbl}</div>
                </div>
              ))}
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
              <button className="sa-btn" style={S.btnOutline} onClick={rematch}>↺ REMATCH</button>
              <button className="sa-btn" style={S.btnPrimary} onClick={() => { setResultOverlay(null); setPage('select'); }}>
                ← SELECT BIRD
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  root:             { minHeight:'100vh', background:'#050810', color:'#e0e8ff', fontFamily:"'Rajdhani',sans-serif", overflowX:'hidden' as const, position:'relative' as const },
  gridOverlay:      { position:'fixed' as const, top:0, left:0, width:'100%', height:'100%', backgroundImage:'linear-gradient(rgba(0,200,255,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(0,200,255,0.025) 1px,transparent 1px)', backgroundSize:'50px 50px', pointerEvents:'none' as const, zIndex:0 },
  inner:            { position:'relative' as const, zIndex:1, maxWidth:1100, margin:'0 auto', padding:'0 16px 80px' },
  header:           { textAlign:'center' as const, padding:'36px 20px 16px' },
  logoMain:         { fontFamily:"'Bebas Neue',cursive", fontSize:'clamp(36px,8vw,82px)', letterSpacing:6, background:'linear-gradient(135deg,#ff6b1a,#ff3d6e,#ffd700)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text', filter:'drop-shadow(0 0 28px rgba(255,107,26,0.55))', lineHeight:1 },
  logoSub:          { fontFamily:"'Share Tech Mono',monospace", fontSize:'clamp(10px,2vw,14px)', color:'#00c8ff', letterSpacing:8, marginTop:4, opacity:0.75 },
  headerLine:       { width:260, height:1, margin:'14px auto 0', background:'linear-gradient(90deg,transparent,#ff6b1a,#ff3d6e,transparent)' },
  centeredPage:     { maxWidth:560, margin:'0 auto', padding:'10px 0' },
  connectCard:      { background:'rgba(10,15,28,0.9)', border:'1px solid rgba(0,200,255,0.12)', borderRadius:18, padding:'clamp(24px,5vw,48px)', backdropFilter:'blur(20px)', textAlign:'center' as const, boxShadow:'0 8px 40px rgba(0,0,0,0.5)' },
  heroWrap:         { width:110, height:110, margin:'0 auto 20px' },
  chainBadge:       { display:'inline-flex', alignItems:'center', gap:6, background:'rgba(168,85,247,0.1)', border:'1px solid rgba(168,85,247,0.3)', borderRadius:20, padding:'4px 14px', fontFamily:"'Share Tech Mono',monospace", fontSize:11, color:'#a855f7', marginBottom:18, letterSpacing:2 },
  dot:              { width:8, height:8, borderRadius:'50%', flexShrink:0 },
  connectTitle:     { fontFamily:"'Bebas Neue',cursive", fontSize:34, letterSpacing:4, color:'#00c8ff', marginBottom:8 },
  connectDesc:      { fontSize:15, color:'rgba(200,220,255,0.65)', lineHeight:1.65, marginBottom:22 },
  walletInput:      { width:'100%', background:'rgba(0,200,255,0.04)', border:'1px solid rgba(0,200,255,0.14)', borderRadius:8, padding:'13px 15px', color:'#e0e8ff', fontFamily:"'Share Tech Mono',monospace", fontSize:13, transition:'border-color 0.2s', boxSizing:'border-box' as const },
  errorMsg:         { background:'rgba(255,60,110,0.08)', border:'1px solid rgba(255,60,110,0.28)', borderRadius:7, padding:'10px 14px', color:'#ff6b88', fontFamily:"'Share Tech Mono',monospace", fontSize:12, marginTop:10, textAlign:'left' as const, lineHeight:1.5 },
  contractNote:     { marginTop:16, fontFamily:"'Share Tech Mono',monospace", fontSize:10, color:'rgba(100,130,180,0.4)', wordBreak:'break-all' as const },
  btnPrimary:       { background:'linear-gradient(135deg,#ff6b1a,#ff3d6e)', color:'#fff', border:'none', borderRadius:9, fontFamily:"'Bebas Neue',cursive", fontSize:20, letterSpacing:3, padding:'13px 30px', cursor:'pointer', boxShadow:'0 4px 22px rgba(255,107,26,0.35)', transition:'all 0.18s', display:'inline-block' },
  btnOutline:       { background:'transparent', border:'1px solid #00c8ff', color:'#00c8ff', borderRadius:9, fontFamily:"'Bebas Neue',cursive", fontSize:18, letterSpacing:2, padding:'11px 26px', cursor:'pointer', transition:'all 0.18s' },
  btnSmall:         { background:'transparent', border:'1px solid rgba(0,200,255,0.3)', color:'#00c8ff', borderRadius:6, fontFamily:"'Bebas Neue',cursive", fontSize:13, letterSpacing:1, padding:'5px 12px', cursor:'pointer', transition:'all 0.18s' },
  spinner:          { width:18, height:18, border:'2px solid rgba(255,255,255,0.2)', borderTopColor:'#fff', borderRadius:'50%' },
  selectPage:       { paddingTop:8 },
  walletBar:        { display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' as const, background:'rgba(0,200,255,0.04)', border:'1px solid rgba(0,200,255,0.1)', borderRadius:9, padding:'10px 14px', marginBottom:18 },
  tabBar:           { display:'flex', gap:4, background:'rgba(0,0,0,0.3)', border:'1px solid rgba(0,200,255,0.08)', borderRadius:10, padding:4, marginBottom:22 },
  tab:              { flex:1, padding:'9px 6px', textAlign:'center' as const, fontFamily:"'Bebas Neue',cursive", fontSize:14, letterSpacing:1.5, cursor:'pointer', borderRadius:7, color:'rgba(150,180,220,0.55)', transition:'all 0.18s', background:'none', border:'none' },
  sectionTitle:     { fontFamily:"'Bebas Neue',cursive", fontSize:26, letterSpacing:4, color:'#00c8ff', marginBottom:16 },
  nftGrid:          { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(min(170px,100%),1fr))', gap:14, marginBottom:24 },
  lbRow:            { display:'flex', alignItems:'center', gap:10, padding:'11px 14px', borderRadius:8, marginBottom:5, background:'rgba(0,0,0,0.2)', border:'1px solid rgba(0,200,255,0.08)', transition:'background 0.18s', cursor:'default' },
  arenaPage:        { paddingTop:8 },
  arenaBar:         { display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap' as const, gap:10, background:'rgba(10,15,28,0.85)', border:'1px solid rgba(0,200,255,0.1)', borderRadius:10, padding:'12px 16px', marginBottom:16, backdropFilter:'blur(10px)' },
  arenaGrid:        { display:'grid', gridTemplateColumns:'1fr 80px 1fr', gap:12, alignItems:'start', marginBottom:14 },
  vsColumn:         { display:'flex', flexDirection:'column' as const, alignItems:'center', justifyContent:'center', paddingTop:60, gap:10 },
  battleLog:        { background:'rgba(8,12,22,0.88)', border:'1px solid rgba(0,200,255,0.1)', borderRadius:10, padding:'10px 12px', height:'clamp(90px,14vh,140px)', overflowY:'auto' as const, marginBottom:14, fontFamily:"'Share Tech Mono',monospace", fontSize:12, lineHeight:1.9 },
  logEntry:         { animation:'sa-fadeSlide 0.3s ease' },
  actionGrid:       { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:7 },
  actionBtn:        { background:'rgba(5,8,20,0.8)', border:'1px solid rgba(0,200,255,0.12)', borderRadius:9, padding:'10px 4px', textAlign:'center' as const, color:'#e0e8ff', fontFamily:"'Rajdhani',sans-serif", transition:'all 0.18s' },
  actionBtnSpecial: { border:'1px solid rgba(255,215,0,0.25)', background:'rgba(255,215,0,0.04)' },
  resultBg:         { position:'fixed' as const, inset:0, background:'rgba(0,0,0,0.82)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, backdropFilter:'blur(8px)', padding:20 },
  resultCard:       { background:'#0d1426', border:'1px solid rgba(0,200,255,0.15)', borderRadius:20, padding:'clamp(24px,5vw,48px)', textAlign:'center' as const, maxWidth:480, width:'100%', boxShadow:'0 16px 60px rgba(0,0,0,0.6)' },
  rewardItem:       { background:'rgba(255,215,0,0.05)', border:'1px solid rgba(255,215,0,0.18)', borderRadius:10, padding:'12px 20px', textAlign:'center' as const },
};